import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { ManageEvaluations } from './manage-evaluations.component';
import { groupByPosition, sortByJersey } from './manage-evaluations.helpers';
import { IFormOption } from '../../_core/_ui/forms.component';
import { IRecordScoreInput, IRecordUserActionsResults } from './manage-evaluations.interface';
import { useQueueOpen } from '../../../lib/stores/connectivity.store';
import { useCompanySport } from '../../../lib/stores/company.store';
import { getAuthUser } from '../../../lib/services/auth.service';
import { bufferScoreAction, forEachBufferedAction, scoreFromAction, transformScoreOperationToAction } from '../../../lib/utils/user-session.service';
import { hasAnyRoles } from '../../../lib/services/user.service';
import { useBufferedScoreSubmit } from '../../../lib/services/score-submit.service';
import { usePendingScoreBuffer } from '../../../lib/hooks/use-pending-score-buffer';
import fpSet from 'lodash/fp/set';
import fpUnset from 'lodash/fp/unset';
import { restoreFailedScoreRemoval, revertFailedScoreWrite } from '../../../lib/helpers/score-buffer.helpers';
import { scoreCellKey, scoreCellPath } from './score-cell.helpers';
import { ROLES } from '../../../../_configuration';

interface IManageEvaluationsContainer {
  assessmentSession?: IAssessmentSession;
  drills: IDrill[];
  players: IPlayerAssessment[];
  jerseys: { [key: string]: IJersey };
  scores: IScore[];
  loading: boolean;
  submitScores: () => Promise<IRecordUserActionsResults | undefined>
  hasUnsavedScores: boolean;
  setHasUnsavedScores: (value: boolean) => void;
  isSaving: boolean;
}

export const ManageEvaluationsContainer: React.FC<IManageEvaluationsContainer> = ({
  assessmentSession,
  drills,
  players,
  jerseys,
  scores,
  loading,
  submitScores,
  hasUnsavedScores,
  setHasUnsavedScores,
  isSaving
}) => {
  const user = getAuthUser()

  const isQueueOpen = useQueueOpen();
  const [showReview, setShowReview] = useState(false);
  const [scoreHash, setScoreHash] = useState({});
  const { pendingScoreKeys, trackScoreWrite, trackScoreRemoval } = usePendingScoreBuffer();
  const [activeRound, setActiveRound] = useState(1);
  const [activeDrill, setActiveDrill] = useState<IDrill | undefined>();
  const [drillsById, setDrillsById] = useState<{ [key: string]: IDrill }>({});
  const [drillOptions, setDrillOptions] = useState<IFormOption[]>([]);
  const [activePlayer, setActivePlayer] = useState<IPlayerAssessment | undefined>();
  const [playersByPosition, setPlayersByPosition] = useState({});
  const [playerOptions, setPlayerOptions] = useState<IFormOption[]>([]);
  const [playersById, setPlayersById] = useState<{ [key: string]: IPlayerAssessment }>({});
  const [playersLinear, setPlayersLinear] = useState<IPlayerAssessment[]>([]);
  const [currPlayerIndex, setCurrPlayerIndex] = useState(0);
  const [stateLoaded, setStateLoaded] = useState(false);
  const numJerseys = _.keys(jerseys).length;

  const sport = useCompanySport()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (players.length && numJerseys > 0) {
      const nextPlayersByPosition = groupByPosition(players, sport);
      _.each(nextPlayersByPosition, (players, position) => {
        nextPlayersByPosition[position] = sortByJersey(nextPlayersByPosition[position], jerseys);
      });

      const positions = _.keys(nextPlayersByPosition);
      const firstPlayer = _.get(nextPlayersByPosition, [positions[0], '0']);
      if (firstPlayer) {
        setActivePlayer(firstPlayer);
      }
      setPlayersByPosition(nextPlayersByPosition);
      setPlayersById(_.keyBy(players, '_id'))

      const nextPlayerOptions: any[] = [];
      _.each(nextPlayersByPosition, (players, position) => {
        nextPlayerOptions.push({
          label: position,
          options: _.map(players, player => ({ value: player._id, label: `${_.capitalize(jerseys[player._id]?.color)} - ${jerseys[player._id]?.number}`}))
        });
      });
      setPlayerOptions(nextPlayerOptions);

      setPlayersLinear(_.flatten(_.values(nextPlayersByPosition)));

      setStateLoaded(true);
    }
  }, [players.length, numJerseys]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const createScoreHash = async () => {
      const nextScoreHash = {};

      if (user) {
        // Create initial score hash
        _.each(scores, score => {
          _.set(nextScoreHash, [score.playerAssessmentId, score.drillId, score.skillId, score.round], score);
        });

        // Apply every buffered score action (oldest-first) on top of the seed hash
        await forEachBufferedAction(user, action => {
          if (action.actionType === "score" || action.actionType === "removeScore") {
            const scoreAction = (action.payload as {score: IScore}).score

            // On score, find and updated latest score hash for target
            if (action.actionType === "score") {
              const currScore = _.get(nextScoreHash, [scoreAction.playerAssessmentId, scoreAction.drillId, scoreAction.skillId, scoreAction.round]) as IScore | undefined
              const updatedScore: IScore = {...scoreAction, _id: currScore?._id ?? scoreAction._id, clientId: currScore?.clientId ?? scoreAction.clientId}
              _.set(nextScoreHash, [scoreAction.playerAssessmentId, scoreAction.drillId, scoreAction.skillId, scoreAction.round], updatedScore);
            }
            // On remove, unset score
            else if (action.actionType === "removeScore")
              _.unset(nextScoreHash, [scoreAction.playerAssessmentId, scoreAction.drillId, scoreAction.skillId, scoreAction.round]);
          }
        })

        setScoreHash(nextScoreHash);
      }
    }

    // A buffer read that times out keeps the grid as it was; F-01 decides what a failed rebuild shows.
    if (!loading) createScoreHash().catch((error) => console.error('createScoreHash failed; keeping the current score grid', error))
  }, [loading, scores, user]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (drills.length) {
      const nextDrillOptions: IFormOption[] = drills.map(drill => ({
        value: drill._id,
        label: drill.name
      }));

      setDrillsById(_.keyBy(drills, '_id'));
      setDrillOptions(nextDrillOptions);
      setActiveDrill(drills[0]);
    }
  }, [assessmentSession, drills.length, setActiveDrill]);

  const handleGetScoreStatus = useCallback((playerAssessment: IPlayerAssessment, drillId: string) => {
    let expectedSkillCount = 0;
    const activeSkillIds: string[] = []
    _.each(drillsById[drillId].skillSets, (skillSet => {
      if (_.includes(skillSet.positionIds, playerAssessment.position._id)) {
        _.each(skillSet.skills, skill => {
          if (!skill.deletedAt) {
            expectedSkillCount += 1;
            activeSkillIds.push(skill._id)
          }
        })
      }
    }))
    const skillScores = _.get(scoreHash, [playerAssessment._id, drillId], {});
    let currSkillCount = 0;

    _.each(skillScores, (roundScores, skillId): void => {
      if (roundScores && roundScores[activeRound] && activeSkillIds.includes(skillId)) {
        currSkillCount += 1;
      }
    });

    if (currSkillCount === expectedSkillCount) return 'FILLED';
    if (currSkillCount > 0) return 'PARTIAL';
    return 'NONE';
  }, [activeRound, drillsById, scoreHash]);

  const handleSetActivePlayer = useCallback((player: IPlayerAssessment) => {
    setCurrPlayerIndex(_.findIndex(playersLinear, currPlayer => currPlayer._id === player._id));
    setActivePlayer(player);
  }, [playersLinear]);

  // A drill change starts scoring at round 1; the review-scores table passes its own round.
  const handleSetActiveDrill = useCallback((drill: IDrill, round = 1) => {
    setActiveDrill(drill);
    setActiveRound(round);
  }, []);

  const handleRemoveScore = useCallback((score: IRecordScoreInput) => {
    const path = scoreCellPath(score);
    // The widget passes the value it is removing. Keep that snapshot even if this
    // handler still has a scoreHash from before the latest render.
    const previous = score;
    const { buffered } = bufferScoreAction(user, score, transformScoreOperationToAction, { remove: true });
    trackScoreRemoval(scoreCellKey(score), buffered, () => {
      setScoreHash(prev => restoreFailedScoreRemoval(prev, path, previous));
    });
    setScoreHash(prev => fpUnset(path, prev));
    setHasUnsavedScores(true);
  }, [user, setHasUnsavedScores, trackScoreRemoval]);

  const handleRecordScore = useCallback((score: IRecordScoreInput) => {
    const path = scoreCellPath(score);
    const { loggedAt, clientId, buffered } = bufferScoreAction(user, score, transformScoreOperationToAction);
    trackScoreWrite(scoreCellKey(score), buffered, () => {
      setScoreHash(prev => revertFailedScoreWrite(prev, path, loggedAt));
    });
    setScoreHash(prev => fpSet(path, {...score, loggedAt, clientId}, prev));
    setHasUnsavedScores(true);
  }, [user, setHasUnsavedScores, trackScoreWrite]);

  // Owns only this session's non-calibration actions: calibration scores reuse the same
  // buffer tagged with calibrationAssignmentId and belong to the Assignments screen.
  const isMyAction = useCallback(
    (action: IUserAction) => {
      const score = scoreFromAction(action);
      return score?.assessmentSessionId === assessmentSession?._id && !score?.additionalInfo?.calibrationAssignmentId;
    },
    [assessmentSession?._id],
  );

  const { hasSaveError, handleSubmitScores } = useBufferedScoreSubmit({
    user, isSaving, isQueueOpen, hasUnsavedScores, setHasUnsavedScores, submitScores, isMyAction,
    canSubmit: !!assessmentSession,
  });

  const canCheckIn = hasAnyRoles({roles: [ROLES.CHECK_IN], assessmentId: assessmentSession?.assessmentId})

  return <ManageEvaluations
    loading={loading || !stateLoaded}
    session={assessmentSession}
    jerseys={jerseys}
    removeScore={handleRemoveScore}
    recordScore={handleRecordScore}
    submitScores={handleSubmitScores}
    playersByPosition={playersByPosition}
    playersById={playersById}
    playersLinear={playersLinear}
    currPlayerIndex={currPlayerIndex}
    setCurrPlayerIndex={setCurrPlayerIndex}
    playerOptions={playerOptions}
    drills={drills}
    drillsById={drillsById}
    drillOptions={drillOptions}
    activePlayer={activePlayer}
    setActivePlayer={handleSetActivePlayer}
    activeDrill={activeDrill}
    setActiveDrill={handleSetActiveDrill}
    activeRound={activeRound}
    setActiveRound={setActiveRound}
    getScoreStatus={handleGetScoreStatus}
    scoreHash={scoreHash}
    pendingScoreKeys={pendingScoreKeys}
    showReview={showReview}
    setShowReview={setShowReview}
    canCheckIn={canCheckIn}
    hasUnsavedScores={hasUnsavedScores}
    hasSaveError={hasSaveError}
    isSaving={isSaving}
  />
};
